import { Refresh } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  SelectProps,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Breadcrumb from "../App/Components/Navigation/Subcomponents/Breadcrumbs/Breadcrumbs";
import { PageContainer } from "../App/Components/PageContainer/PageContainer";
import { PageTitle } from "../App/Components/PageTitle/PageTitle";
import { useArchivedFieldValidation } from "../App/Hooks/useArchivedFieldValidation";
import { BaseFormFieldWithHistory } from "../App/Typings/Campaigns";
import { DynamicFilterRules } from "./Components/DynamicFilterRules";
import { DynamicGroupPreviewModal } from "./Components/DynamicGroupPreviewModal";
import { CommandHelpContent, FieldHelpContent } from "./Components/HelpContent";
import { DynamicGroupState } from "./Components/State/DynamicGroupState";

export interface FieldType {
  id: string;
  field: BaseFormFieldWithHistory;
}

export function DynamicGroup() {
  const {
    getters: {
      groupId,
      groupName,
      rules,
      isLoading,
      previewLoading,
      previewOpen,
      previewMembers,
      selectedCampaign,
      resetToSavedRules,
      rulesAreValid,
      pendingChanges,
      tags,
      tagsLoading,
      formsLoading,
      paginationState,
      customFields,
      forms,
      groups,
      isGroupsLoading,
    },
    setters: { setGroupName, setPreviewOpen, setSelectedCampaign, resetRules },
    handlers: { handleClosePreview, handleGroupSave, handlePreviewClick, handleSetRules },
  } = DynamicGroupState();

  const {
    archivedFields,
    archivedFieldIds,
    showArchivedFields,
    toggleArchivedFields,
    hasArchivedFields,
    activeFormFields,
    hasValidState,
    enableSaveFilters,
    enablePreview,
  } = useArchivedFieldValidation({
    storageKey: `showArchivedFields.${groupId}`,
    rules,
    customFields,
    forms,
    groupName,
    rulesAreValid,
  });

  return !isLoading && !tagsLoading && !formsLoading && !isGroupsLoading ? (
    <PageContainer>
      <Breadcrumb pageTitle={groupName} />
      <PageTitle
        sx={{
          lineHeight: 1,
          m: 1,
          color: "secondary.main",
        }}
      >
        Edit Dynamic Group Rules
      </PageTitle>
      <Paper sx={{ mt: { xs: 1, md: 2 }, p: { xs: 2, lg: 4 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Grid container spacing={2}>
            <Grid
              item
              md={8}
              xs={12}
              style={{ backgroundColor: "white", border: "none" }}
              sx={{ alignItems: "center" }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormControl>
                  <FormLabel htmlFor="groupname-text">Group Name</FormLabel>
                  <TextField
                    id="groupname-text"
                    variant="outlined"
                    hiddenLabel={true}
                    value={groupName || ""}
                    onChange={(v) => setGroupName(v.target.value)}
                    sx={{ width: "300px", marginRight: 5 }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="groupcampaign-select">Form</FormLabel>
                  <TextField
                    id="groupcampaign-select"
                    variant="outlined"
                    hiddenLabel={true}
                    value={selectedCampaign || "none"}
                    onChange={(v) => {
                      setSelectedCampaign(v.target.value);
                      resetRules();
                    }}
                    style={{ width: "300px" }}
                    select
                    SelectProps={
                      {
                        MenuProps: {
                          "data-test": "dynamic-group-form-select",
                        },
                      } as Partial<SelectProps>
                    }
                  >
                    <MenuItem value="none" key={`group-campaign-none`}>
                      None
                    </MenuItem>
                    {forms &&
                      forms.map((c) => (
                        <MenuItem value={c.campaignId} key={`group-campaign-${c.campaignId}`}>
                          {c.campaignName}
                        </MenuItem>
                      ))}
                  </TextField>
                </FormControl>
                {archivedFields.length > 0 && (
                  <Box sx={{ ml: 2, justifySelf: "center" }}>
                    <FormControlLabel
                      label="Include Archived Form Fields"
                      data-test="toggle-archived-form-fields"
                      control={
                        <Switch
                          checked={showArchivedFields}
                          size="small"
                          onChange={toggleArchivedFields}
                          color="info"
                        />
                      }
                      sx={{
                        ml: 0,
                        justifySelf: "center",
                        "& .MuiFormControlLabel-label": { whiteSpace: "nowrap" },
                      }}
                    />
                    <Stack
                      flexDirection="row"
                      justifyContent="flex-end"
                      alignItems="center"
                      gap={1}
                      sx={{ width: "100%" }}
                    >
                      {!hasValidState && (
                        <FormHelperText error data-test="error-invalid-rule">
                          Please ensure all filters have a valid value before saving.
                        </FormHelperText>
                      )}
                      {hasArchivedFields && !showArchivedFields && (
                        <FormHelperText error data-test="error-archived-fields-selected">
                          Choose non-archived fields from filters or enable "Include Archived Form Fields".
                        </FormHelperText>
                      )}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid
              item
              md={4}
              xs={12}
              style={{ backgroundColor: "white", border: "none", textAlign: "right" }}
            >
              {pendingChanges && (
                <Tooltip title="Reset to saved rules" arrow>
                  <IconButton
                    onClick={resetToSavedRules}
                    data-test="dynamic-group-reset-btn"
                    sx={{ marginRight: 1 }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
              )}
              <Button
                color="primary"
                variant="contained"
                size="large"
                onClick={enableSaveFilters ? handleGroupSave : undefined}
                disabled={!enableSaveFilters}
                data-test="dynamic-group-save-btn"
              >
                Save
              </Button>
              &nbsp;
              <Button
                color="primary"
                variant="contained"
                size="large"
                onClick={enablePreview ? handlePreviewClick : undefined}
                disabled={!enablePreview}
                data-test="dynamic-group-preview-btn"
              >
                Preview
              </Button>
              &nbsp;
              <Button color="primary" variant="contained" size="large" onClick={handleClosePreview}>
                Cancel
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Divider style={{ marginBottom: "10px" }} />
            </Grid>
            <DynamicFilterRules
              rules={rules}
              onChange={(rules) => handleSetRules(rules)}
              fieldOptions={activeFormFields}
              helpTitle="Dynamic Group Rules"
              helpId="dynamic-group-help"
              campaigns={forms ?? []}
              tags={tags}
              groups={groups?.items ?? []}
              archivedFieldIds={archivedFieldIds}
              showArchivedFieldError={!showArchivedFields}
              FieldHelpContent={
                <FieldHelpContent
                  headerContent={
                    <>
                      <Typography variant="body2">
                        Fields are candidate information that you can search on such as their name or
                        location.
                      </Typography>
                      <Typography variant="body2">
                        You can select a form above to search within a specific form's custom fields.
                      </Typography>
                    </>
                  }
                />
              }
              CommandHelpContent={
                <CommandHelpContent
                  headerContent={
                    <>
                      <Typography variant="body2">
                        Commands are operations you can perform on a field to find candidates.
                      </Typography>
                      <Typography variant="body2">
                        The commands can change based on the field you are searching on.
                      </Typography>
                    </>
                  }
                />
              }
            />
          </Grid>
        </Box>
      </Paper>
      <DynamicGroupPreviewModal
        isLoading={previewLoading}
        isOpen={previewOpen}
        members={previewMembers?.items ?? []}
        totalMembers={previewMembers?.totalItems ?? 0}
        onClose={() => setPreviewOpen(false)}
        pagination={paginationState}
      />
    </PageContainer>
  ) : (
    <PageContainer>
      <Paper sx={{ mt: { xs: 1, md: 4 }, p: { xs: 2, lg: 4 } }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </Paper>
    </PageContainer>
  );
}
